/* @flow */
import * as React from "react";
import { Link } from "../common/Link";
import styled, { css } from "styled-components";
import { CloseIcon } from "../Icons/index";
import { TourContext } from "../Tour";

const Flex = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: flex-start;
  align-content: flex-start;
`;

const Container = styled(Flex)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  max-height: 100%;
  transform: translate3d(0, 0, 0);
  overflow: auto;
  background-color: ${({ darkMode, theme }) =>
    darkMode ? theme.popupDarkBackground : theme.toggleAccountDarkBg};
  color: white;
  z-index: ${({ tour }) => (tour ? "auto" : "100000")};

  ${({ isCompensation }) =>
    isCompensation &&
    css`
      @media (min-width: 960px) {
        top: 50%;
        left: 50%;
        right: auto;
        bottom: auto;
        transform: translateX(-50%) translateY(-50%);
        border: 2px solid #ededed;
      }
    `};
`;

const Header = styled(Flex)`
  justify-content: space-between;
  align-content: center;
  align-items: center;
  flex-grow: 0;
  flex-shrink: 0;
  width: 100%;
  height: 45px;
  padding: 0 15px;
  padding-right: 60px;
  background-color: ${({ darkMode, theme }) =>
    darkMode ? theme.popupDarkBackground : theme.toggleAccountDarkBg};
  border-bottom: ${({ darkMode, theme }) =>
    darkMode ? "none" : `1px solid ${theme.mainSeparator}`};
`;

const Heading = styled.h2`
  font-size: 16px;
  line-height: 1;
  color: ${({ theme }) => theme.foreground};
`;

const IconContainer = styled.i`
  position: absolute;
  top: 12px;
  right: 15px;
  width: 22px;
  height: 21px;
  color: #99959d;
  cursor: pointer;
  transition: all 0.3s ease;

  :hover {
    color: ${({ theme }) => theme.foreground};
  }
`;

const Inner = styled(Flex)`
  flex-grow: 1;
  flex-shrink: 1;
  width: 100%;

  ${(props) =>
    props.big
      ? css`
          @media (orientation: portrait) {
            min-height: 100%;
          }
        `
      : css`
          height: 100%;
          max-height: calc(100% - 45px);
        `};

  ${({ mobileColumn }) =>
    mobileColumn &&
    css`
      flex-direction: column;
      justify-content: center;
      align-items: center;
    `};
`;

const TournamentsInner = styled(Inner)`
  ${({ big }) =>
    !big &&
    css`
      max-height: 100%;
    `};

  ${({ nowrap }) =>
    nowrap &&
    css`
      align-content: center;
      align-items: center;
      height: auto;
      max-height: none;
      min-height: 100%;
    `};
`;

type Props = {
  onClick?: () => void
};

function Button({ onClick }: Props) {
  return (
    <IconContainer>
      <CloseIcon width="21px" onClick={onClick} />
    </IconContainer>
  );
}

function CloseButton({ onClick }: Props) {
  if (typeof onClick === "function") {
    return <Button onClick={onClick} />;
  }

  return (
    <Link replace to="/trade">
      <Button />
    </Link>
  );
}

type PopupProps = {
  className?: string,
  type?: string,
  title: React.Node,
  children: React.Node | Array<React.Node>,
  onClick?: () => void,
  darkMode?: boolean,
  big?: boolean
};

class Popup extends React.Component<PopupProps> {
  componentDidMount() {
    if (document.body) document.body.style.overflow = "hidden";
  }

  componentWillUnmount() {
    if (document.body) document.body.style.overflow = "";
  }

  render() {
    const {
      title,
      onClick,
      children,
      type,
      isTour,
      darkMode,
      isCompensation,
      mobileColumn,
    } = this.props;
    const tour = this.context;

    switch (type) {
      case "tournaments":
        return (
          <Container className={this.props.className} tour={tour}>
            <CloseButton onClick={onClick} />
            <TournamentsInner
              mobileColumn={mobileColumn}
              nowrap={this.props.nowrap}
              big={this.props.big}
            >
              {children}
            </TournamentsInner>
          </Container>
        );
      default:
        return (
          <Container
            tour={tour}
            ref={this.props.forwardedRef}
            darkMode={darkMode}
            isCompensation={isCompensation}
          >
            <Header darkMode={darkMode}>
              <Heading>{title}</Heading>
              {!isTour && <CloseButton onClick={onClick} />}
            </Header>
            <Inner mobileColumn={mobileColumn}>{children}</Inner>
          </Container>
        );
    }
  }
}

Popup.contextType = TourContext;

export default React.forwardRef((props, ref) => (
  <Popup {...props} forwardedRef={ref} />
));



// WEBPACK FOOTER //
// ./src/components/TradeMobile/Popup.js